import React, { useMemo, useState } from 'react'
import { Link as RouterLink } from 'react-router-dom'

import { Box, Stack, Tooltip, Typography } from '@mui/material'
import { Term } from 'components/feedback/Term'
import { useTheme } from '@mui/material/styles'
import {
  ArrowUpRightIcon,
  CheckIcon,
  MegaphoneIcon,
  NewspaperIcon,
  RotateCcwIcon,
  SparklesIcon,
  XIcon,
} from 'lucide-react'

import { Alert, Button, Input, Modal } from '@repo/ui'
import { CompanyGroupCell, EmptyState, StatusChip } from 'components/display'
import { QueryErrorState } from 'components/feedback'
import { Table } from 'components/display/Table/Table'
import { ActionDropdown, ActionDropdownItem } from 'components/input/ActionDropdown'
import { MARKET_ITEM_STATUSES, MARKET_STAT_STATUSES } from 'constants/statuses'
import {
  useCreateMarketItem,
  useExtractClaims,
  useMarketItemsQuery,
  useSetMarketItemReview,
} from 'hooks/api'
import { ROUTES } from 'routes'
import { dateOnly, getErrorMessage } from 'utils'
import type { IMarketItem } from 'services/marketItemsApi'

/**
 * The Content tab of `/internal/market` (VRG-461, epic VRG-445).
 *
 * ## Why "content" and not "articles"
 *
 * The corpus already ingests things that are not articles — a video, a podcast page, a
 * report PDF landing page — and it will ingest more of them as sources widen. Naming
 * the surface after the commonest case made every other case read as a mistake.
 *
 * The three-level model this tab sits in the middle of:
 *   SOURCE  (a feed)          1 -> N
 *   CONTENT (this tab)        1 -> N
 *   CLAIMS  (the Claims tab)
 * Content can also be added standalone, which is what the Add button does.
 */
interface MarketContentTabProps {
  /** Owned by the shell so the Add button can live in the tab row's action slot. */
  addOpen: boolean
  onOpenAdd: () => void
  onCloseAdd: () => void
}

export const MarketContentTab = ({ addOpen, onOpenAdd, onCloseAdd }: MarketContentTabProps) => {
  const theme = useTheme()

  const [paginationModel, setPaginationModel] = useState({ page: 0, pageSize: 50 })
  const [sortField, setSortField] = useState<string | undefined>(undefined)
  const [sortDir, setSortDir] = useState<'asc' | 'desc' | undefined>(undefined)
  const [search, setSearch] = useState('')

  const { data, isLoading, isError, error, refetch } = useMarketItemsQuery({
    limit: paginationModel.pageSize,
    offset: paginationModel.page * paginationModel.pageSize,
    sort: sortField && sortDir ? `${sortField}:${sortDir}` : undefined,
    q: search || undefined,
  })
  const items = data?.data ?? []
  const total = data?.total ?? 0

  const createItem = useCreateMarketItem()
  const setReview = useSetMarketItemReview()
  const extractStats = useExtractClaims()

  const [actionError, setActionError] = useState<string | null>(null)
  const [actionNote, setActionNote] = useState<string | null>(null)
  const [addUrl, setAddUrl] = useState('')
  const [addError, setAddError] = useState<string | null>(null)

  /** Every row-action mutation funnels through here — same contract as
   * InternalAnnouncementsPage's `run()`: `mutateAsync` so a rejection is actually
   * caught here rather than swallowed into the mutation's own unread error state. */
  const run = async (fn: () => Promise<unknown>) => {
    setActionError(null)
    setActionNote(null)
    try {
      await fn()
    } catch (e) {
      setActionError(getErrorMessage(e))
    }
  }

  const closeAdd = () => {
    onCloseAdd()
    setAddUrl('')
    setAddError(null)
  }

  const handleAdd = async () => {
    const url = addUrl.trim()
    if (!url) return
    setAddError(null)
    try {
      await createItem.mutateAsync(url)
      closeAdd()
    } catch (e) {
      setAddError(getErrorMessage(e) || 'Could not add that URL.')
    }
  }

  const rowActions = (row: IMarketItem): ActionDropdownItem[] => [
    // Every row carries a leading icon (§21).
    {
      label: 'Mark kept',
      icon: <CheckIcon strokeWidth={1} />,
      visible: row.status !== 'kept',
      onClick: () => run(() => setReview.mutateAsync({ id: row.id, status: 'kept' })),
    },
    {
      label: 'Mark rejected',
      icon: <XIcon strokeWidth={1} />,
      visible: row.status !== 'rejected',
      onClick: () => run(() => setReview.mutateAsync({ id: row.id, status: 'rejected' })),
    },
    {
      label: 'Mark unreviewed',
      icon: <RotateCcwIcon strokeWidth={1} />,
      visible: row.status !== 'unreviewed',
      onClick: () => run(() => setReview.mutateAsync({ id: row.id, status: 'unreviewed' })),
      divider: true,
    },
    {
      label: row.gtmSignal ? 'Unflag GTM signal' : 'Flag as GTM signal',
      icon: <MegaphoneIcon strokeWidth={1} />,
      onClick: () => run(() => setReview.mutateAsync({ id: row.id, gtmSignal: !row.gtmSignal })),
      divider: true,
    },
    {
      label: 'Extract claims',
      icon: <SparklesIcon strokeWidth={1} />,
      // RECORD STATE, so hidden rather than disabled (§46): an item with no body text
      // (an OpenGraph-only page) has nothing to extract from, and the resolving action
      // is not something an admin grants. Extraction is also a BILLED model call, so
      // offering it where it is guaranteed to return nothing spends money for no
      // possible result.
      visible: Boolean(row.bodyText),
      onClick: () =>
        run(async () => {
          const result = await extractStats.mutateAsync(row.id)
          setActionNote(
            result.skipped
              ? `${row.title ?? 'Content'}: nothing to extract (${result.skipped.replace('_', ' ')}).`
              : `${row.title ?? 'Content'}: ${result.proposed} proposed for review` +
                (result.rejectedByVerification
                  ? `, ${result.rejectedByVerification} discarded as unverifiable`
                  : '') +
                '.',
          )
        }),
    },
  ]

  const columns = useMemo(
    () => [
      {
        field: 'source',
        headerName: 'Source',
        type: 'string',
        width: 140,
        sortable: false,
        // Almost always "Manual" today — the poller that populates market_sources is
        // VRG-462. Shown anyway so the column already reads correctly once it lands.
        renderCell: ({ row }: { row: IMarketItem }) => (
          <Typography variant="body3" color="text.secondary" noWrap>
            {row.sourceName ?? 'Manual'}
          </Typography>
        ),
      },
      {
        field: 'title',
        headerName: 'Title',
        type: 'string',
        /*
          An authored WIDTH, not `flex: 2`. Measured after the Companies column and the
          wider "Vera Ask approved" header landed: title rendered at **20px** — an
          unreadable sliver — because the fixed columns now sum past the grid and the one
          flex column absorbed the entire shortfall.

          `Table`'s `withProportionalFlex` exists to handle exactly this, but its first
          line bails out if ANY column declares `flex`, on the correct reasoning that a
          hand-authored responsive layout must not be silently re-cut. So this table was
          opting itself out of the protection it needed. With every column authored, the
          documented behaviour applies: proportional when there is room, frozen at these
          widths with horizontal scroll when there is not — which is the right outcome for
          a nine-column table at 1280 (§49 notes some tables already scroll there).
        */
        width: 300,
        sortable: true,
        // VRG-537 — opens the detail page (problem/solution, companies) rather than
        // the article itself. The article is still one click away from there; this
        // row is where the corpus is BROWSED, not where an article is read.
        renderCell: ({ row }: { row: IMarketItem }) => (
          <Stack
            component={RouterLink}
            to={ROUTES.INTERNAL_MARKET_CONTENT(row.id)}
            direction="row"
            alignItems="center"
            spacing={1}
            sx={{ color: 'inherit', textDecoration: 'none', minWidth: 0 }}
          >
            <Typography variant="body3" sx={{ fontWeight: 600 }} noWrap>
              {row.title || row.url}
            </Typography>
          </Stack>
        ),
      },
      {
        field: 'publication',
        headerName: 'Publication',
        type: 'string',
        width: 160,
        sortable: true,
        // Linked to the SITE's origin, not the article — Title already links to the
        // specific article; this is "whose site is this", derived from the stored
        // article URL rather than a separate stored field.
        renderCell: ({ row }: { row: IMarketItem }) => {
          if (!row.publication) {
            return (
              <Typography variant="body3" color="text.secondary">
                —
              </Typography>
            )
          }
          let origin: string | null = null
          try {
            origin = new URL(row.url).origin
          } catch {
            origin = null
          }
          return (
            <Stack
              component={origin ? 'a' : 'span'}
              href={origin ?? undefined}
              target={origin ? '_blank' : undefined}
              rel={origin ? 'noreferrer' : undefined}
              direction="row"
              alignItems="center"
              spacing={0.5}
              sx={{ color: 'text.secondary', textDecoration: 'none', minWidth: 0 }}
            >
              <Typography variant="body3" color="text.secondary" noWrap>
                {row.publication}
              </Typography>
              {origin && (
                <ArrowUpRightIcon strokeWidth={1} size={14} style={{ flexShrink: 0, opacity: 0.6 }} />
              )}
            </Stack>
          )
        },
      },
      {
        field: 'published_at',
        headerName: 'Date',
        // A number column needs room for the HEADER, not the values — measured, not
        // guessed (§49).
        width: 150,
        type: 'dateTime',
        sortable: true,
        valueGetter: (value: unknown, row: IMarketItem) =>
          row.publishedAt ? new Date(row.publishedAt) : null,
        // `date()` takes the ISO string, not the `Date` the valueGetter above
        // returns (that conversion is for the grid's own dateTime typing) — reads
        // straight off `row.publishedAt` rather than re-stringifying `value`.
        renderCell: ({ row }: { row: IMarketItem }) => (row.publishedAt ? dateOnly(row.publishedAt) : '—'),
      },
      {
        field: 'status',
        headerName: 'Status',
        type: 'string',
        width: 130,
        sortable: true,
        // VRG-741 — a backfill item is reference only, so it shows that instead of "Unreviewed"
        // (it never enters the review queue).
        renderCell: ({ row }: { row: IMarketItem }) =>
          row.backfill && row.status === 'unreviewed' ? (
            <Typography variant="body3" color="text.secondary" noWrap>
              <Term id="backfill-item">Reference only</Term>
            </Typography>
          ) : (
            <StatusChip statuses={MARKET_ITEM_STATUSES} status={row.status} size="small" />
          ),
      },
      {
        field: 'gtm_signal',
        headerName: 'GTM signal',
        type: 'boolean',
        width: 120,
        sortable: false,
        renderCell: ({ row }: { row: IMarketItem }) =>
          row.gtmSignal ? (
            <CheckIcon strokeWidth={1} size={18} color={theme.palette.success.main} />
          ) : (
            <Typography variant="body3" color="text.secondary">
              —
            </Typography>
          ),
      },
      {
        /*
          STATS — two numbers, because either alone misleads. "3 extracted" says the
          model found figures here; "1 approved" says a human agreed with one of them.
          Extracted-only reads as though the figures are usable when nothing has been
          checked; approved-only hides that this row has a queue waiting on it.

          A dash when nothing has been extracted, matching every other column, rather
          than "0 extracted" — which is a fact nobody needs stated 20 times down a page.
        */
        field: 'stats',
        headerName: 'Claims',
        type: 'string',
        // 200, up from 150. The approved CHIP plus "N extracted" does not fit 150 — it
        // truncated to "1 ex…" the first time it rendered. Sized for the CONTENT here
        // rather than the header (§49's usual failure is the inverse, a number column too
        // narrow for its own header; this column's content is the wider of the two).
        width: 200,
        sortable: false,
        renderCell: ({ row }: { row: IMarketItem }) => {
          if (row.statsExtracted === 0) {
            return (
              <Typography variant="body3" color="text.secondary">
                —
              </Typography>
            )
          }
          /*
            Approved LEADS, as a chip; extracted follows as muted context.

            The approved count is the only one that means anything downstream — it is what
            Ask Vera can cite — so it earns the emphasis and the leading position, and the
            chip makes it scannable down a column where most rows have none. "Extracted"
            is the denominator: useful for judging a run, not a result in itself.

            No interpunct between them. A `·` between a chip and plain text reads as a
            third element; the chip's own edge already separates the two.

            The chip goes through the shared `StatusChip` rather than a hand-rolled pill
            (§28) so it inherits the dark-mode tone derivation every other status token in
            the app uses — a local `<Chip>` would paint the light-mode pastel in dark mode,
            which is the exact drift `statusTone` was extracted to end. It takes its label
            from the map, so the count is passed as the label.
          */
          return (
            <Stack direction="row" alignItems="center" spacing={2} sx={{ minWidth: 0 }}>
              {row.statsApproved > 0 && (
                <StatusChip
                  statuses={{
                    approved: {
                      ...MARKET_STAT_STATUSES.approved,
                      label: `${row.statsApproved} approved`,
                    },
                  }}
                  status="approved"
                  size="small"
                />
              )}
              <Typography variant="body3" color="text.secondary" noWrap>
                {row.statsExtracted} extracted
              </Typography>
            </Stack>
          )
        },
      },
      {
        // VRG-537 AC3 — which companies this content is assigned to, without
        // opening the row. `CompanyGroupCell` is the same avatar-stack component
        // InternalUsersPage already uses for the same shape of data.
        field: 'companies',
        headerName: 'Companies',
        type: 'string',
        // 200, up from 150. The cell can now carry avatars plus two counts; at 150 the
        // "N suggested" text clipped. The HEADER still fits comfortably (§49).
        width: 200,
        sortable: false,
        // `?? []` is load-bearing, not defensive noise. The DTO types `companies` as
        // always present, so TypeScript is satisfied — but a response predating this
        // field (an API that has not restarted, a cached payload, a rolling deploy
        // where the client ships first) omits it, and an unguarded `.length` here
        // throws inside a grid cell, which React escalates to the ErrorBoundary and
        // takes the WHOLE PAGE white. A missing column is a blank cell; it should
        // never be a blank app.
        renderCell: ({ row }: { row: IMarketItem }) => {
          const companies = row.companies ?? []
          if (companies.length === 0) {
            return (
              <Typography variant="body3" color="text.secondary">
                —
              </Typography>
            )
          }
          /*
            VRG-573 — the avatar stack shows CONFIRMED companies only, with a pending
            count beside it.

            Putting suggestions in the stack would be the same mistake `in_corpus` made:
            the surface would assert a relationship nobody has agreed to. An avatar in
            this column reads as "this content is for that brand", and for a suggestion
            that is not yet true. The count says work is waiting without claiming an
            answer, and clicking through is where the decision gets made.
          */
          /*
            Three states, three renderings — and the counts come from `state`, never from
            arithmetic on the total.

            The first version computed `suggested = companies.length - confirmed.length`,
            where `confirmed` had already been narrowed to rows WITH a company id. A
            confirmed OFF-FILE brand therefore fell into the suggested count: the jewelry
            article showed "10 suggested" with nine suggestions and one confirmed Cartier,
            and the confirmed row appeared nowhere at all. Derive each count from the thing
            it counts.

            Only resolved-confirmed rows get avatars, and they are mapped to `{ id }`
            because `CompanyGroupCell` links via `company.subdomain ?? company.id` — VRG-573
            renamed this ref's `id` to `companyId`, so passing the ref through yields a link
            to `/internal/companies/undefined`, which looks right until someone clicks it.

            Off-file confirmed brands are the PROSPECT signal, so they get their own count
            rather than being folded into either neighbour. Suggestions get a count and no
            avatar: an avatar here reads as "this content is for that brand", which is not
            yet true of a guess — the same overclaim `in_corpus` made.
          */
          const confirmedResolved = companies
            .filter(c => c.state === 'confirmed' && c.companyId)
            .map(c => ({ id: c.companyId, name: c.name, logo: c.logo }))
          const offFile = companies.filter(c => c.state === 'confirmed' && !c.companyId)
          const suggested = companies.filter(c => c.state === 'suggested').length
          return (
            <Stack direction="row" alignItems="center" spacing={2} sx={{ minWidth: 0 }}>
              {confirmedResolved.length > 0 && <CompanyGroupCell companies={confirmedResolved} />}
              {offFile.length > 0 && (
                <Tooltip title={`Confirmed, not a company on file: ${offFile.map(c => c.name).join(', ')}`}>
                  <Typography variant="body3" color="text.secondary" noWrap>
                    {offFile.length} off file
                  </Typography>
                </Tooltip>
              )}
              {suggested > 0 && (
                <Tooltip title="Proposed by extraction, not yet confirmed">
                  <Typography variant="body3" color="text.secondary" noWrap>
                    {suggested} suggested
                  </Typography>
                </Tooltip>
              )}
            </Stack>
          )
        },
      },
      {
        field: 'actions',
        headerName: '',
        width: 64,
        sortable: false,
        filterable: false,
        disableColumnMenu: true,
        disableReorder: true,
        align: 'center',
        pinned: 'right',
        renderCell: ({ row }: { row: IMarketItem }) => <ActionDropdown items={rowActions(row)} />,
      },
    ],
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [theme],
  )

  return (
    <Box>
      {actionError && (
        <Typography variant="body3" sx={{ color: theme.palette.error.main, lineHeight: 1.5, mb: 3 }}>
          {actionError}
        </Typography>
      )}

      {actionNote && (
        <Typography variant="body3" sx={{ color: theme.palette.text.secondary, lineHeight: 1.5, mb: 3 }}>
          {actionNote}
        </Typography>
      )}

      {isError ? (
        <QueryErrorState
          error={error}
          subject="market content"
          permissionLabel="View the market-data corpus"
          onRetry={() => refetch()}
        />
      ) : (
        <Table
          loading={isLoading}
          columns={columns}
          rows={items}
          getRowId={(row: IMarketItem) => row.id}
          fillBottom
          onSearchChange={(value: string) => {
            setSearch(value)
            setPaginationModel(prev => ({ ...prev, page: 0 }))
          }}
          paginationMode="server"
          rowCount={total}
          paginationModel={paginationModel}
          onPaginationModelChange={model => setPaginationModel(model)}
          sortingMode="server"
          onSortModelChange={model => {
            const first = model[0]
            if (first?.sort) {
              setSortField(first.field)
              setSortDir(first.sort)
            } else {
              setSortField(undefined)
              setSortDir(undefined)
            }
            setPaginationModel(prev => ({ ...prev, page: 0 }))
          }}
          emptyState={
            <EmptyState
              icon={<NewspaperIcon strokeWidth={1} />}
              title="No content yet"
              text="Paste a URL to fetch a page and add it. Extraction fills in what it can, and you fill in the rest. Register a feed on the Sources tab to have content arrive on its own."
              buttons={
                <Button variant="primary" onClick={onOpenAdd}>
                  Add content
                </Button>
              }
            />
          }
        />
      )}

      <Modal
        open={addOpen}
        title="Add content"
        onClose={closeAdd}
        description="Fetches the page and extracts a title, author, publication and date from its structured metadata when the page has any. Works for an article, a video page or a report landing page. A page with nothing structured is still added — fill in the gaps by hand afterward."
        actions={
          <>
            <Button variant="secondary" onClick={closeAdd}>
              Cancel
            </Button>
            <Button
              variant="primary"
              onClick={handleAdd}
              disabled={!addUrl.trim()}
              loading={createItem.isPending}
            >
              {createItem.isPending ? 'Reading page' : 'Add'}
            </Button>
          </>
        }
      >
        {addError && <Alert severity="error" description={addError} sx={{ mt: 1, mb: 3 }} />}
        <Input
          label="Content URL"
          placeholder="https://example.com/article"
          value={addUrl}
          onChange={(value: string) => {
            setAddUrl(value)
            if (addError) setAddError(null)
          }}
          handleKeyDown={(event: React.KeyboardEvent) => {
            if (event.key === 'Enter' && !createItem.isPending) handleAdd()
          }}
          error={Boolean(addError)}
          disabled={createItem.isPending}
        />
      </Modal>
    </Box>
  )
}
